import { Head, router } from "@inertiajs/react"
import {
  MoreHorizontal,
  Plus,
  RotateCw,
  ShieldCheck,
  ShieldOff,
  Trash2,
  Users,
} from "lucide-react"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { ListPagination } from "@/components/admin/list-pagination"
import { ListShell } from "@/components/admin/list-shell"
import { InviteTeammateDialog } from "@/components/agency/invite-teammate-dialog"
import AgencySettingsLayout from "@/components/agency/settings/agency-settings-layout"
import { EmptyState } from "@/components/empty-state"
import { StatusBadge } from "@/components/status-badge"
import { TierBadge } from "@/components/tier-badge"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useMenuDialogHandoff } from "@/hooks/use-menu-dialog-handoff"
import { useOnline } from "@/hooks/use-online"
import AppLayout from "@/layouts/app-layout"
import { toAppLocale } from "@/lib/i18n/locales"
import { formatRelativeIntl } from "@/lib/relative-time"
import {
  agencyMemberRolePath,
  agencySettingsTeamPath,
  invitationPath,
  resendInvitationPath,
} from "@/routes"
import type { AgencyTeamIndexProps, AgencyTeamRow } from "@/types/agency"

/**
 * Agency/Team/Index — interleaved list of accepted members + live
 * invitations for the current agency (OKS-13 BE TRD §API + AC3).
 *
 * Backend ships a single `rows[]` array with a `kind: "user" |
 * "invitation"` discriminator and server-namespaced `id` strings
 * (`"user:42"` / `"invitation:7"`). Sort is server-side alphabetical by
 * `name.to_s.downcase` — UI does NOT re-sort. `status` is server-derived
 * (`active` for users, `pending` or `expired` for invitations) and never
 * recomputed from `expires_at < now`.
 *
 * Per-row actions (`Resend`, `Revoke`) live in a `<DropdownMenu>` and
 * appear ONLY for invitation rows where `can_manage === true`. Member
 * rows never expose actions — agency-side member removal is deferred to
 * an OKS-14 follow-up. Revoke is gated by an `<AlertDialog>` with Cancel
 * autofocused per Crew destructive-dialog convention; Resend is
 * optimistic with toast-on-click + error rollback.
 *
 * Locked QA testid (OKS-13 v4 freeze):
 *   - `agency-team-list` (page region)
 *   - `agency-team-row[data-member-id={"user:N"}]` for member rows
 *   - `agency-team-row[data-invitation-id={"invitation:N"}]` for invite rows
 *   - `agency-tier-badge[data-state]` per row
 *   - `agency-status-badge[data-state]` per row
 *   - `agency-team-row-actions[data-invitation-id]` on the action trigger
 *   - `agency-team-action-resend` / `agency-team-action-revoke-trigger`
 *   - `agency-team-revoke-confirm` (destructive primary)
 *   - `agency-invite-teammate-trigger` on the toolbar Button
 */
export default function AgencyTeamIndex({
  rows,
  pagination,
  can_invite,
  can_manage_role,
  admin_count,
}: AgencyTeamIndexProps) {
  const { t } = useTranslation()

  const isEmpty = rows.length === 0
  // OKS-630 — the actions column renders when the invite gate OR the
  // role-manage gate is true (OR'd so the column never vanishes for an
  // admin). Member-tier viewers (both gates false) get NO column — the
  // header + every cell are DOM-absent (UX Spec §Accessibility).
  const showActionsColumn = can_invite || can_manage_role

  const onPageChange = useCallback(
    (page: number) =>
      router.get(
        agencySettingsTeamPath(),
        { page },
        {
          preserveState: true,
          preserveScroll: true,
          only: ["rows", "pagination"],
        },
      ),
    [],
  )

  return (
    <AppLayout>
      <Head title={t("agency.team.title")} />
      {/* OKS-672 — Team page now renders inside the Agency Settings shell
          (settings heading + sub-nav aside + content), `wide` so the
          members/invitations data table keeps its legibility. Page
          contents below are unchanged from the standalone version. */}
      <AgencySettingsLayout wide>
        <ListShell
          testId="agency-team-list"
          title={t("agency.team.title")}
          subtitle={t("agency.team.subtitle_total", {
            count: pagination.total,
          })}
          action={
            can_invite ? (
              <InviteTeammateDialog
                trigger={
                  <Button
                    type="button"
                    data-testid="agency-invite-teammate-trigger"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    {t("agency.team.dialog.invite_teammate.trigger")}
                  </Button>
                }
              />
            ) : undefined
          }
          footer={
            isEmpty ? undefined : (
              <ListPagination
                page={pagination.page}
                perPage={pagination.per_page}
                total={pagination.total}
                onPageChange={onPageChange}
              />
            )
          }
        >
          {isEmpty ? (
            <EmptyState
              icon={Users}
              title={t("agency.team.empty_fresh.title")}
              description={t("agency.team.empty_fresh.body")}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("agency.team.column.name")}</TableHead>
                  <TableHead>{t("agency.team.column.email")}</TableHead>
                  <TableHead>{t("agency.team.column.tier")}</TableHead>
                  <TableHead>{t("agency.team.column.status")}</TableHead>
                  <TableHead>{t("agency.team.column.sent_at")}</TableHead>
                  {showActionsColumn && (
                    <TableHead className="text-right">
                      <span className="sr-only">
                        {t("agency.team.column.actions")}
                      </span>
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TeamRow
                    key={row.id}
                    row={row}
                    canManage={can_invite}
                    canManageRole={can_manage_role}
                    adminCount={admin_count}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </ListShell>
      </AgencySettingsLayout>
    </AppLayout>
  )
}

interface TeamRowProps {
  row: AgencyTeamRow
  /**
   * Page-level invite gate (can_invite). Used to suppress the entire
   * actions cell — member-tier viewers don't render the column at all
   * (DOM-absent contract). Invitation rows still consult `row.can_manage`
   * for fine-grained gating.
   */
  canManage: boolean
  /**
   * OKS-630 — page-level gate for the user-row role actions ("Make
   * admin" / "Remove admin"). Same DOM-absent contract: member-tier
   * viewers never see the menu on user rows.
   */
  canManageRole: boolean
  /** OKS-630 — live agency admin count; drives the sole-admin lock. */
  adminCount: number
}

/**
 * Single team row — discriminates on `row.kind` to choose the per-row
 * `data-{member,invitation}-id` attribute and decide whether to mount
 * the action `DropdownMenu`.
 *
 * Two attributes on the same `<TableRow>` mirror the QA v4 contract:
 *   - `data-testid="agency-team-row"` — universal selector
 *   - `data-member-id="user:N"` for `kind: "user"`
 *   - `data-invitation-id="invitation:N"` for `kind: "invitation"`
 *
 * Tier rendering — server-authoritative on `row.tenant_role`. Backend
 * projects the field per row (commit `4db2401`); `<TierBadge>` reads
 * it directly with no FE-side derivation. The Vitest contract test
 * asserts `data-state` mirrors `row.tenant_role` 1:1.
 *
 * Invitation rows render an em-dash placeholder rather than a tier
 * badge — UX-Lead decision: tier is informational only at acceptance,
 * not at invitation time. The wire still carries the intended tier
 * (kept symmetric for future "tier locked at acceptance" UX hooks).
 */
function TeamRow({ row, canManage, canManageRole, adminCount }: TeamRowProps) {
  const { t } = useTranslation()
  const isInvitation = row.kind === "invitation"
  const idAttr = isInvitation
    ? { "data-invitation-id": row.id }
    : { "data-member-id": row.id }
  const showActionsCell = canManage || canManageRole

  return (
    <TableRow data-testid="agency-team-row" {...idAttr}>
      <TableCell className="font-medium">{row.name}</TableCell>
      <TableCell className="text-muted-foreground">{row.email}</TableCell>
      <TableCell>
        {isInvitation ? (
          <span className="text-muted-foreground" aria-hidden="true">
            —
          </span>
        ) : (
          <TierBadge tier={row.tenant_role} testId="agency-tier-badge" />
        )}
      </TableCell>
      <TableCell>
        <StatusBadge status={row.status} testId="agency-status-badge" />
      </TableCell>
      <TableCell className="text-muted-foreground">
        {row.sent_at ? (
          <RelativeSentAt iso={row.sent_at} />
        ) : (
          <span aria-hidden="true">{t("agency.team.sent_at_dash")}</span>
        )}
      </TableCell>
      {showActionsCell && (
        <TableCell className="text-right">
          {isInvitation ? (
            canManage && row.can_manage ? (
              <RowActions row={row} />
            ) : null
          ) : canManageRole ? (
            <MemberRowActions row={row} adminCount={adminCount} />
          ) : null}
        </TableCell>
      )}
    </TableRow>
  )
}

/**
 * Row-actions dropdown. Mounts only for invitation rows where
 * `can_manage === true`. Resend dispatches optimistic toast + Inertia
 * POST; Revoke opens an `<AlertDialog>` with Cancel autofocused.
 */
function RowActions({ row }: { row: AgencyTeamRow }) {
  const { t } = useTranslation()
  const online = useOnline()
  const [revokeOpen, setRevokeOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()

  // Strip the `"invitation:"` prefix to get the raw numeric id for the
  // route helper. Backend's `invitations` resource expects an integer,
  // not the namespaced row id.
  const numericId = Number(row.id.replace(/^invitation:/, ""))

  function handleResend() {
    if (submitting || !online) return
    toast.success(t("agency.toast.invitation_resent"))
    setSubmitting(true)
    router.post(
      resendInvitationPath({ id: numericId }),
      {},
      {
        preserveScroll: true,
        onError: () => toast.error(t("agency.errors.server")),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function handleRevoke() {
    if (submitting || !online) return
    setSubmitting(true)
    router.delete(invitationPath({ id: numericId }), {
      preserveScroll: true,
      onSuccess: () => {
        toast.success(t("agency.toast.invitation_revoked"))
        setRevokeOpen(false)
      },
      onError: () => toast.error(t("agency.errors.server")),
      onFinish: () => setSubmitting(false),
    })
  }

  // Offline-tooltip pattern mirrors OKS-12's PendingFirstAdminInviteCard
  // Resend/Revoke buttons. Disabled `<button>`s don't dispatch
  // pointer/focus events in the DOM, so Radix Tooltip needs an
  // intermediate `<span tabIndex={0}>` wrapper to receive them. Sighted
  // users see why the trigger is greyed out; SR users hear it via
  // `aria-disabled` + the visible TooltipContent (announced via
  // `aria-describedby` Radix wires automatically).
  const triggerButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={t("agency.team.row_actions_aria", { email: row.email })}
      data-testid="agency-team-row-actions"
      data-invitation-id={row.id}
      disabled={submitting || !online}
      aria-disabled={submitting || !online ? "true" : undefined}
    >
      <MoreHorizontal className="size-4" aria-hidden="true" />
    </Button>
  )

  return (
    <>
      {!online ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <span tabIndex={0} className="inline-flex">
                {triggerButton}
              </span>
            }
          />
          <TooltipContent>{t("agency.team.offline_tooltip")}</TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger render={triggerButton} />
          <DropdownMenuContent align="end" finalFocus={finalFocus}>
            <DropdownMenuItem
              onClick={() => {
                handleResend()
              }}
              data-testid="agency-team-action-resend"
              aria-label={t("agency.team.action.resend_aria", {
                email: row.email,
              })}
            >
              <RotateCw className="size-4" aria-hidden="true" />
              {t("agency.team.action.resend")}
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() => {
                yieldFocusToDialog()
                setRevokeOpen(true)
              }}
              data-testid="agency-team-action-revoke-trigger"
              aria-label={t("agency.team.action.revoke_aria", {
                email: row.email,
              })}
            >
              <Trash2 className="size-4" aria-hidden="true" />
              {t("agency.team.action.revoke")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("agency.team.revoke_confirm.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("agency.team.revoke_confirm.body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div
            className="text-foreground bg-muted/40 rounded-md border px-3 py-2 text-sm font-medium break-all"
            data-testid="agency-team-revoke-email"
          >
            {row.email}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>
              {t("agency.team.revoke_confirm.cancel")}
            </AlertDialogCancel>
            {/* OKS-1023 — plain `Button`: `AlertDialogAction` is Base UI
                `Dialog.Close` and would close before the request settles. */}
            <Button
              type="button"
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={handleRevoke}
              disabled={submitting || !online}
              aria-disabled={submitting || !online ? "true" : undefined}
              data-testid="agency-team-revoke-confirm"
            >
              {t("agency.team.revoke_confirm.submit")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/**
 * OKS-630 — Per-user role actions ("Make admin" / "Remove admin").
 * Mounts only on `kind: "user"` rows when the page-level
 * `can_manage_role` gate is true (member-tier viewers never reach it —
 * DOM-absent, UX Spec §Accessibility). Direct mirror of the brand-side
 * `MemberRowActions` (OKS-460), adapted to the agency page's base-ui
 * conventions (`render` props + `useMenuDialogHandoff` focus handoff).
 *
 * Behaviour by current tier:
 *   - member → **"Make admin"**: direct `PATCH … { tenant_role: "admin" }`.
 *     No client toast — the success toast is server-driven via the
 *     use-flash → sonner bridge after the partial reload confirms
 *     (UX Spec §Micro-Interactions).
 *   - admin → **"Remove admin"**: opens an `<AlertDialog>` confirm
 *     (Cancel autofocused, destructive primary). When the row is the
 *     SOLE admin (`adminCount <= 1`) the item is disabled + tooltip
 *     ("An agency must keep at least one admin") — stays present with
 *     `aria-disabled` so SR users hear the reason (UX Spec §A11y).
 *     The server enforces the same guardrail regardless.
 *
 * Self-demotion (`isSelf`) adds an extra "you'll lose access" warning
 * inside the confirm dialog. The server returns member-tier props on
 * the partial reload, so the actions disappear after a self-demote.
 *
 * Offline: the trigger is disabled + tooltipped, mirroring `RowActions`.
 */
function MemberRowActions({
  row,
  adminCount,
}: {
  row: AgencyTeamRow
  adminCount: number
}) {
  const { t } = useTranslation()
  const online = useOnline()
  const { currentUser } = useCurrentContext()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()

  // Strip the `"user:"` prefix → raw User id for `agencyMemberRolePath`.
  const numericId = Number(row.id.replace(/^user:/, ""))
  const isAdmin = row.tenant_role === "admin"
  const isLastAdmin = isAdmin && adminCount <= 1
  const isSelf = currentUser?.id === numericId

  function patchRole(tenantRole: "admin" | "member", onDone?: () => void) {
    if (submitting || !online) return
    setSubmitting(true)
    router.patch(
      agencyMemberRolePath({ id: numericId }),
      { tenant_role: tenantRole },
      {
        preserveScroll: true,
        onSuccess: () => onDone?.(),
        onError: () => toast.error(t("agency.errors.server")),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  const triggerButton = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={t("agency.team.action.row_aria", { email: row.email })}
      data-testid="agency-team-member-actions"
      data-member-id={row.id}
      disabled={submitting || !online}
      aria-disabled={submitting || !online ? "true" : undefined}
    >
      <MoreHorizontal className="size-4" aria-hidden="true" />
    </Button>
  )

  return (
    <>
      {!online ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <span tabIndex={0} className="inline-flex">
                {triggerButton}
              </span>
            }
          />
          <TooltipContent>{t("agency.team.offline_tooltip")}</TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger render={triggerButton} />
          <DropdownMenuContent align="end" finalFocus={finalFocus}>
            {!isAdmin ? (
              <DropdownMenuItem
                onClick={() => {
                  patchRole("admin")
                }}
                data-testid="agency-team-action-make-admin"
                aria-label={t("agency.team.action.make_admin_aria", {
                  email: row.email,
                })}
              >
                <ShieldCheck className="size-4" aria-hidden="true" />
                {t("agency.team.action.make_admin")}
              </DropdownMenuItem>
            ) : isLastAdmin ? (
              // Sole admin: present-but-disabled with an explanatory
              // tooltip (NOT DOM-absent) so the rule is discoverable +
              // SR-announced (UX Spec §A11y). `span` wrapper lets the
              // Tooltip receive events the disabled item won't dispatch.
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span tabIndex={0} className="inline-flex w-full">
                      <DropdownMenuItem
                        variant="destructive"
                        disabled
                        aria-disabled="true"
                        data-testid="agency-team-action-remove-admin"
                        data-last-admin="true"
                      >
                        <ShieldOff className="size-4" aria-hidden="true" />
                        {t("agency.team.action.remove_admin")}
                      </DropdownMenuItem>
                    </span>
                  }
                />
                <TooltipContent>
                  {t("agency.team.action.last_admin_tooltip")}
                </TooltipContent>
              </Tooltip>
            ) : (
              <DropdownMenuItem
                variant="destructive"
                onClick={() => {
                  yieldFocusToDialog()
                  setConfirmOpen(true)
                }}
                data-testid="agency-team-action-remove-admin"
                aria-label={t("agency.team.action.remove_admin_aria", {
                  email: row.email,
                })}
              >
                <ShieldOff className="size-4" aria-hidden="true" />
                {t("agency.team.action.remove_admin")}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("agency.team.action.remove_admin_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("agency.team.action.remove_admin_confirm_body", {
                name: row.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {isSelf && (
            <div
              className="text-destructive bg-destructive/10 rounded-md border px-3 py-2 text-sm font-medium"
              data-testid="agency-team-remove-admin-self-warning"
            >
              {t("agency.team.action.remove_admin_confirm_self")}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              autoFocus
              data-testid="agency-team-remove-admin-cancel"
            >
              {t("agency.team.action.remove_admin_confirm_cancel")}
            </AlertDialogCancel>
            {/* OKS-1023 — plain `Button`: `AlertDialogAction` is Base UI
                `Dialog.Close` and would close before the request settles. */}
            <Button
              type="button"
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={() => patchRole("member", () => setConfirmOpen(false))}
              disabled={submitting || !online}
              aria-disabled={submitting || !online ? "true" : undefined}
              data-testid="agency-team-remove-admin-confirm"
            >
              {t("agency.team.action.remove_admin_confirm_submit")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/**
 * Localised relative sent-at label, via `formatRelativeIntl`.
 */
function RelativeSentAt({ iso }: { iso: string }) {
  const { t, i18n } = useTranslation()
  return (
    <span>
      {t("agency.team.sent_at_value", {
        relative: formatRelativeIntl(iso, toAppLocale(i18n.language)),
      })}
    </span>
  )
}
